import { cast, flow, getParent, getParentOfType, Instance, types } from 'mobx-state-tree';
import { CoreApi } from 'service/axios-api';
import { IFeedStore } from '.';

const ContentOne = types
    .model('ContentOne', {
        contentId: types.number,
        channelId: types.string,
        channelName: types.string,
        channelThumbnail: types.string,
        title: types.string,
        thumbnailImage: types.string,
        platformContentId: types.string,
        gameName: types.string,

        keyword: types.string,
        sentence: types.string,
        // platformViewCount: types.number,
        // subscriberCountNum: types.number,
        feedback: types.optional(types.number, 0),
        reported: types.optional(types.boolean, false),
    })
    .views((self) => ({
        get parent(): IContentCuration {
            return getParentOfType(self, ContentCuration);
        },
    }))
    .views((self) => ({
        get productId(): string {
            return self.parent.productId;
        },
    }))
    .actions((self) => ({
        feedbackUodo() {
            self.feedback = 0;
            CoreApi.delete(`/content-recommendation/v1/content/feedback`, {
                data: { contentId: self.contentId },
                headers: { 'X-Product-ID': self.productId },
            });
        },
    }))
    .actions((self) => ({
        fetch: flow(function* () {
            const {
                data: { feedback },
            }: { data: { feedback: number } } = yield CoreApi.get('/content-recommendation/v1/content/feedback', {
                headers: { 'X-Product-ID': self.productId },
                params: {
                    content_id: self.contentId,
                },
            });
            self.feedback = feedback;
        }),
        watch() {
            const { contentId, keyword, sentence } = self;
            self.parent.apiPost('content/watch', { contentId, keyword, sentence });
        },
        like() {
            if (self.feedback === 1) return self.feedbackUodo();
            self.feedback = 1;
            self.parent.apiPost('content/feedback', { contentId: self.contentId, feedback: self.feedback });
        },
        dislike() {
            if (self.feedback === -1) return self.feedbackUodo();
            self.feedback = -1;
            self.parent.apiPost('content/feedback', { contentId: self.contentId, feedback: self.feedback });
        },
        report(type: number, comment: string = '') {
            self.parent.apiPost('content/feedback', { contentId: self.contentId, type, ...(type === 6 ? { comment } : {}) });
            self.reported = true;

            if (self.parent.filtered.length < 10) {
                self.parent.feedMore();
            }
            // console.log('----------->', self.parent.filtered.length);
        },
    }));

const ContentCuration = types
    .model('ContentsRecommendationStore', {
        list: types.optional(types.array(ContentOne), []),
        state: types.optional(types.string, 'loading'),
        showDialog: types.optional(types.boolean, false),
    })
    .views((self) => ({
        get parent(): IFeedStore {
            return getParent<IFeedStore>(self);
        },
    }))
    .views((self) => ({
        get productId(): string {
            return self.parent.productId;
        },
        get serviceId(): string {
            return self.parent.productLibrary?.currentBranch?.serviceId || self.parent.productCmsPc?.publicBranch.serviceId || '';
        },
        get filtered() {
            return self.list.filter((x) => !x.reported); //.slice(0, 10); // only 10 videos
        },
    }))
    .actions((self) => ({
        apiPost: async function (route: string, data = {}) {
            return await CoreApi.post(
                `/content-recommendation/v1/${route}`,
                { trackingServiceId: self.serviceId, location: 'default', ...data },
                {
                    headers: { 'X-Product-ID': self.productId },
                }
            );
        },
    }))
    .actions((self) => ({
        fetch: async function (limit: number = 1) {
            const { data }: { data: [{ contents: [] }] } = await self.apiPost('contents', { limit });
            const list = data.reduce(
                (prev, current: any) =>
                    prev.concat(current.contents.map((x: any) => ({ ...x, keyword: current.keyword, sentence: current.sentence }))),
                []
            );

            // since fetch() is called in component's init, fetched data will be displayed right away, so below logic replaces self.display
            const contents = list.map((x: any) => ({ contentId: x.contentId }));
            self.apiPost('contents/display', { contents });
            ///

            return list;
        },
        // display() {
        //     const contents = self.list.map((x) => ({ contentId: x.contentId }));
        //     self.apiPost('contents/display', { contents });
        // },
    }))
    .actions((self) => ({
        fetchOne: flow(function* () {
            try {
                self.state = 'loading';
                const list = yield self.fetch(1);
                self.list = cast(list);
                self.state = 'done';
            } catch (e) {
                self.state = 'failed';
            }
        }),
        feedList: flow(function* () {
            if (self.list.length > 1) return;
            let list = yield self.fetch(10);
            list = list.filter((x: any) => x.contentId !== self.list[0].contentId);
            self.list = cast([...self.list, ...list]);
        }),
        feedMore: flow(function* () {
            const list = yield self.fetch(5);
            const filtered = list.filter((x: any) => !self.list.map((one) => one.contentId).includes(x.contentId));
            self.list = cast([...self.list, ...filtered]);
            if (self.filtered.length === 0) self.state = 'failed';
        }),
    }));

export interface IContentCuration extends Instance<typeof ContentCuration> {}
export interface IContentOne extends Instance<typeof ContentOne> {}

export default ContentCuration;
